/**
 * 템플릿 접근 권한을 체크합니다.
 *
 * @param requiredPlans - 템플릿에서 요구하는 플랜들
 * @param userPlan - 사용자의 현재 플랜
 * @returns 접근 가능 여부
 *
 * @example
 * checkTemplateAccess(['pro', 'enterprise'], 'pro') // true (접근 가능)
 * checkTemplateAccess(['enterprise'], null) // false (접근 불가)
 * checkTemplateAccess([], null) // true (모든 플랜 허용)
 */
export function checkTemplateAccess(requiredPlans: PlanType[], userPlan: PlanType): boolean {
  // plans가 비어있으면 모든 플랜에서 사용 가능
  if (requiredPlans.length === 0) {
    return true;
  }

  // free 플랜 체크
  if (requiredPlans.includes('Free')) {
    return true;
  }

  // 사용자 플랜이 요구 플랜에 포함되어 있는지
  return requiredPlans.includes(userPlan);
}

/**
 * 서버에서 전달되는 인터랙티브 기술 타입
 */
export type InteractiveTechType =
  | 'nfc'
  | 'photo_frame'
  | 'qrcode'
  | 'photo_scan'
  | 'color_filter'
  | 'overlap'
  | 'gps'
  | 'item'
  | 'chatbot'
  | 'webview'
  | 'forms'
  | 'button_click'
  | 'direction_arrows'
  | 'password';

/**
 * 인터랙티브 기술 표시 정의
 */
export interface InteractiveTechDefinition {
  icon: string;
  label: string;
  type: 'basic' | 'advanced';
}

/**
 * 인터랙티브 기술 타입 매핑
 * - icon: 디자인 시스템의 아이콘 이름
 * - label: 사용자에게 표시될 한글 라벨
 */
export const INTERACTIVE_TECH_MAP: Record<InteractiveTechnology, InteractiveTechDefinition> = {
  Nfc: {
    icon: 'interactive_icons_nfc',
    label: 'NFC 태그 인식',
    type: 'advanced'
  },
  PhotoFrame: {
    icon: 'interactive_icons_photo_frame',
    label: '프레임 촬영',
    type: 'advanced'
  },
  Qr: {
    icon: 'interactive_icons_qrcode',
    label: 'QR 코드 인식',
    type: 'advanced'
  },
  Ar: {
    icon: 'interactive_icons_photo_scan',
    label: 'AR 이미지 인식',
    type: 'advanced'
  },
  ColorFilter: {
    icon: 'interactive_icons_color_filter',
    label: '컬러 필터 렌즈',
    type: 'advanced'
  },
  Overlap: {
    icon: 'interactive_icons_overlap',
    label: '오버랩',
    type: 'advanced'
  },
  Gps: {
    icon: 'interactive_icons_gps',
    label: '특정 장소로 이동',
    type: 'advanced'
  },
  Item: {
    icon: 'interactive_icons_item',
    label: '아이템',
    type: 'basic'
  },
  Chatbot: {
    icon: 'interactive_icons_chatbot',
    label: '챗봇',
    type: 'basic'
  },
  WebView: {
    icon: 'interactive_icons_web_view',
    label: '웹뷰',
    type: 'basic'
  },
  InputText: {
    icon: 'interactive_icons_forms',
    label: '텍스트 입력',
    type: 'basic'
  },
  Button: {
    icon: 'interactive_icons_button_click',
    label: '버튼 클릭',
    type: 'basic'
  },
  InputDirection: {
    icon: 'interactive_icons_direction_arrows',
    label: '방향 입력',
    type: 'basic'
  },
  InputPassword: {
    icon: 'interactive_icons_password',
    label: '비밀번호 입력',
    type: 'basic'
  }
};

/**
 * 인터랙티브 기술 정보 가져오기
 * @param techType - 서버에서 전달받은 기술 타입
 * @returns 기술 정의 또는 undefined
 */
export const getInteractiveTechDefinition = (
  techType: InteractiveTechnology
): InteractiveTechDefinition | undefined => {
  return INTERACTIVE_TECH_MAP[techType];
};
